import { Menu, MenuItem, styled, useTheme } from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { EditorCtx } from "../../wysiwyg/EditorCtx";
import SignUpForm, {
  isSignUpAttribute,
  SignUpAttribute,
} from "../ui/SignUpForm";
import { signUp, SignUpData, useUser } from "../../dataAccess/api/user";
import { splitFullName } from "../../helpers/formatter";
import { signIn, SignInData } from "../../dataAccess/api/token";
import { useLogout } from "../../dataAccess/utils";
import { isTranslationKey, useTranslation } from "../../helpers/i18n";
import { useNavigateSameTab } from "../../helpers/navigation";
import { ServerError } from "../../dataAccess/ServerError";
import { JSONAPIServerError } from "../../dataAccess/JSONAPIServerError";
import { useConfiguration } from "../../dataAccess/api/configuration";
import VerifyEmailFormPopUp from "../ui/VerifyEmailFormPopUp";
import CommunityAccessPopUp from "../ui/CommunityAccessPopUp";
import LoginForm from "../ui/LoginForm";
import { SettingsConfiguration } from "../../builder/Theme";
import { LoginSettings } from "../pages/LogIn";
import { SignupSettings } from "../pages/SignUp";
import Popover from "../ui/Popover";
import { useApiClientContext } from "../../app/ApiClientCtx";

interface Props {
  defaultAction?: "signUp" | "signIn";
  itemSlug?: string;
  textColor?: string;
  desktop: boolean;
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
}

const ProfileActionsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(1.5),
  [theme.breakpoints.up("md")]: {
    marginTop: theme.spacing(5),
  },
}));

const StyledMenuItem = styled(MenuItem)<{ textColor: string }>(
  ({ theme, textColor }) => ({
    color: textColor || theme.palette.text.default,
  }),
);

export function useProfileSettings() {
  const { configuration } = useConfiguration();
  const settings = useMemo(() => {
    let loginSettings: SettingsConfiguration = {};
    let signupSettings: SettingsConfiguration = {};
    configuration?.publishedTemplate?.properties?.pages.forEach((page) => {
      switch (page?.id) {
        case "Login":
          loginSettings = page?.settings;
          break;
        case "SignUp":
          signupSettings = page?.settings;
          break;
        default:
          break;
      }
    });
    return {
      loginSettings: loginSettings as LoginSettings,
      signupSettings: signupSettings as SignupSettings,
    };
  }, [configuration]);
  return settings;
}

function ProfileAccessModal({
  textColor,
  defaultAction,
  itemSlug,
  anchorEl,
  onClose,
  desktop,
}: Props) {
  const router = useRouter();
  const { mocks, isEditor, messages, setCurrentMessage } =
    useContext(EditorCtx);
  const { user: realUser } = useUser();
  const { push: navigate } = useNavigateSameTab();
  let user = realUser;
  if (mocks && "noUserMock" in mocks) {
    user = mocks?.noUserMock;
  }
  const { configuration } = useConfiguration();
  const { loginSettings, signupSettings } = useProfileSettings();
  const { palette } = useTheme();
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [serverError, setServerError] = useState<string>();
  const [signUpErrors, setSignUpErrors] = useState<
    Map<SignUpAttribute, string>
  >(new Map());
  const logout = useLogout(configuration?.isOpenMarketplace);
  const { t } = useTranslation();
  const [currentAction, setCurrentAction] = useState(defaultAction);
  const { APIV3 } = useApiClientContext();

  const clearBackendError = useCallback((field: SignUpAttribute) => {
    setSignUpErrors((prevErrors) => {
      const newErrors = new Map(prevErrors);
      newErrors.delete(field);
      return newErrors;
    });
  }, []);

  useEffect(() => {
    setCurrentAction(defaultAction);
  }, [defaultAction]);

  const onSubmit = useCallback(
    async (data: SignInData) => {
      if (isEditor) {
        setCurrentMessage(messages.previewAlert);
        onClose();
        return;
      }
      setLoading(true);
      try {
        await signIn(APIV3, data).json();
        await navigate(router?.asPath);
        onClose();
      } catch (e) {
        if (e instanceof ServerError) {
          const errorResponse = e;
          setLoginError(errorResponse.getMessage());
        }
      }
      setLoading(false);
    },
    [
      isEditor,
      messages.previewAlert,
      navigate,
      router?.asPath,
      setCurrentMessage,
      onClose,
      APIV3,
    ],
  );

  const onSignUpSubmit = useCallback(
    async (data: SignUpData) => {
      if (isEditor) {
        setCurrentMessage(messages.previewAlert);
        return;
      }

      setLoading(true);
      setSignUpErrors(new Map());
      setServerError(undefined);

      try {
        const { firstName, lastName } = splitFullName(data.fullName);
        data.firstName = firstName;
        data.lastName = lastName;
        data.productSlug = itemSlug;
        await signUp(APIV3, data).json();
        await navigate(router?.asPath);
        onClose();
      } catch (error) {
        if (error instanceof JSONAPIServerError) {
          const errors = error.getAllErrors();
          const newSignUpErrors = new Map<SignUpAttribute, string>();

          errors.forEach((err) => {
            const attribute = err.getAttribute();
            const code = err.getCode();
            if (
              isSignUpAttribute(attribute) &&
              typeof code === "string" &&
              isTranslationKey(code)
            ) {
              newSignUpErrors.set(attribute, t(code));
            }
          });
          setSignUpErrors(newSignUpErrors);
        } else if (error instanceof ServerError) {
          setServerError(error.getMessage());
        } else if (error instanceof Error) {
          setServerError(error.message);
        } else {
          setServerError(t("information_generic_error"));
        }
      } finally {
        setLoading(false);
      }
    },
    [
      isEditor,
      messages.previewAlert,
      setCurrentMessage,
      setLoading,
      setSignUpErrors,
      setServerError,
      itemSlug,
      navigate,
      router?.asPath,
      onClose,
      t,
      APIV3,
    ],
  );

  const form = useMemo(
    () =>
      currentAction === "signIn" ? (
        <LoginForm
          loading={loading}
          onSubmit={onSubmit}
          serverError={loginError}
          closePopUp={onClose}
          setOpenSignUpInProfilePopup={() => setCurrentAction("signUp")}
          settings={loginSettings}
        />
      ) : (
        <SignUpForm
          loading={loading}
          onSubmit={onSignUpSubmit}
          signUpErrors={signUpErrors}
          serverError={serverError}
          closePopUp={onClose}
          setOpenLoginInProfilePopUp={() => setCurrentAction("signIn")}
          settings={signupSettings}
          clearBackendError={clearBackendError}
        />
      ),
    [
      currentAction,
      setCurrentAction,
      loading,
      loginError,
      onSignUpSubmit,
      onSubmit,
      serverError,
      signUpErrors,
      loginSettings,
      signupSettings,
      onClose,
      clearBackendError,
    ],
  );

  const handleLogout = useCallback(() => {
    logout();
    onClose();
  }, [onClose, logout]);

  if (user?.id) {
    return (
      <Menu open anchorEl={anchorEl} onClose={onClose}>
        <StyledMenuItem
          textColor={textColor || palette.text.paper}
          onClick={handleLogout}
        >
          {t("information_logout")}
        </StyledMenuItem>
      </Menu>
    );
  }

  return (
    <Popover anchorEl={anchorEl} onClose={onClose} desktop={desktop}>
      {!user?.id && form}
      <ProfileActionsContainer>
        {user?.id && !user?.confirmed && (
          <VerifyEmailFormPopUp email={user.email} />
        )}
        {user?.confirmed && !user?.organization?.allowedInMarketplace && (
          <CommunityAccessPopUp />
        )}
      </ProfileActionsContainer>
    </Popover>
  );
}

export default ProfileAccessModal;
